<script>
    import { parse } from 'date-fns';

    import API from '../api';
    import main from '../state/main';
    import time from '../state/time';
    import ReservationInfo from './payment/reservation-info.svelte';
    import CreditsInfo from './payment/credits-info.svelte';
    import RecordingInfo from './payment/recording-info.svelte';
    import Alternatives from './payment/alternatives.svelte';
    import Invoice from './payment/invoice.svelte';

    export let id;
    export let code;
    export let type = 'reservation';

    // State management
    let loading = true;
    let saving = false;
    let error = false;
    let errorType = null;

    // Gate target url (redirect with button)
    let gateUrl = null;

    // Got a result in query
    let result = null;

    // Reservation event / credits info
    let credits = null;
    let event = null;
    let recording = null;

    // Client info
    let client = null;

    // Invoice data
    let invoiceUse;
    let invoiceForm;

    // Page type based on route
    const isReservation = (type === 'reservation');
    const isCredits = (type === 'credits');
    const isRecording = (type === 'recording');

    let prefixes = {
        reservation: 'r',
        credits:     'k',
        recording:   'v'
    };
    const prefix = prefixes[type];

    // Parse query
    const query = (() => {
        let parts = (location.search || '?').substr(1).split('&');

        let out = {};
        for (let part of parts) {
            let [key, value] = part.split('=');
            out[key] = value;
        }

        return out;
    })();

    async function onReady() {
        // Already have a result in query
        if (query.result) {
            result = query.result;
            loading = false;
            return;
        }

        // Get payment info
        loading = true;
        let res = await API.preparePayment(type, id, code);
        loading = false;

        // Failed to get payment info
        if (!res) {
            error = true;
            return;
        }

        // Got some error
        if (res.error) {
            error = true;
            errorType = res.errorType;
            return;
        }

        // Success!
        gateUrl = res.url;
        event = res.event;
        credits = res.credits;
        recording = res.recording;
        client = res.client;
    }

    async function gotoGate(e) {
        e.preventDefault();

        try {
            saving = true;
            await API.setInvoice(type, id, code, invoiceUse ? invoiceForm : null);
            location.href = gateUrl;
        } catch (e) {
            saving = false;
        }
    }

    onReady();

    $: onTimeUpdate($time.now, event);
    function onTimeUpdate(curTime, event) {
        if (!event) return;
        if (!isReservation) return;
        if (errorType) return;

        let expTime = parse(event.res_expiration);

        if (curTime > expTime) {
            error = true;
            errorType = 'expired';
            gateUrl = null;
        }
    }

    function withoutQuery(url, param) {
        let ret = new URL(url);
        ret.searchParams.delete(param);

        return ret.toString();
    }
</script>

<form class="modal is-active" on:submit={gotoGate}>
    <div class="modal-background"></div>
    <div class="modal-card">
        <header class="modal-card-head">
            <div class="modal-card-title">
                {#if isReservation}
                    Platba rezervace
                {:else if isCredits}
                    Platba kreditů
                {:else if isRecording}
                    Platba nahrávky
                {/if}
            </div>
        </header>
        <section class="modal-card-body">
            {#if loading}
                <div class="notification is-info">
                    Načítání ...
                </div>
            {:else if result}
                {#if result === 'cancelled'}
                    <div class="notification is-danger">
                        Při platbě došlo k chybě!<br>
                        Zkuste to znovu, později.
                    </div>

                    <a class="button is-primary" href="{withoutQuery(location.href, 'result')}">Zkusit znovu</a>
                {:else if result === 'paid'}
                    <div class="notification is-success">
                        Platba byla úspěšně dokončena! Děkujeme.
                    </div>
                {:else if result === 'pending'}
                    <div class="notification is-warning">
                        Platba se stále zpracovává. Vyčkejte, prosím.
                    </div>
                {:else}
                    <div class="notification is-warning">
                        <!-- TODO: results! -->
                        Neznámý výsledek platby: <b>{result}</b>
                    </div>
                {/if}
            {:else if error}
                <div class="notification is-{errorType === 'paidFor' ? 'warning' : 'danger'}">
                    {#if errorType === 'paidFor'}
                        {#if isReservation}
                            Tato rezervace již byla zaplacena.
                        {:else if isCredits}
                            Tyto kredity již byly zaplaceny.
                        {:else if isRecording}
                            Tato nahrávka již byla zaplacena.
                        {/if}
                    {:else if errorType === 'canceled'}
                        {#if isReservation}
                            Tato rezervace byla zrušena.
                        {:else}
                            Objednávka byla zrušena.
                        {/if}
                    {:else if errorType === 'expired'}
                        Čas na zaplacení vypršel.
                    {:else}
                        Při načítání došlo k chybě.
                    {/if}
                </div>
            {:else}
                {#if isReservation}
                    <ReservationInfo {event} />
                {:else if isCredits}
                    <CreditsInfo {credits} />
                {:else if isRecording}
                    <RecordingInfo {recording} />
                {/if}

                {#if !gateUrl}
                    <Alternatives {client} uid="{prefix}{id}" />
                {:else}
                    <Invoice {id} {code} {type} bind:enabled={invoiceUse} bind:form={invoiceForm} />
                {/if}
            {/if}
        </section>

        <section class="modal-card-foot">
            {#if gateUrl}
                <button type="submit" class="button is-primary" disabled={loading || saving || result || error}>Přejít k platbě</button>
                <small>Tímto souhlasíte se <a href="{$main.baseWeb}/vop#privacy" target="_blank">zpracováním osobních údajů</a></small>
            {:else if result}
                <a class="button" href="/">Přejít do rezervačního systému</a>
            {/if}
        </section>
    </div>
</form>
